<template>
  <div class="smart-nudge-action-drawer flex flex-col h-full">
    <!-- Header -->
    <div class="flex items-center gap-3 py-4 border-b border-surface-200">
      <div class="icon-box" style="background-color: #8b5cf618; color: #8b5cf6;">
        <i class="pi pi-bolt"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="font-medium text-[15px]">{{ suggestion.customerName }}</div>
        <div class="text-xs text-surface-500 mt-0.5">{{ suggestion.serviceNames.join(' + ') }}</div>
      </div>
      <div class="flex items-center gap-1.5">
        <span
          v-if="suggestion.isInLoyaltyProgram"
          class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700"
        >מועדון</span>
        <span
          v-if="suggestion.daysOverdue > 0"
          class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700"
        >באיחור {{ suggestion.daysOverdue }} ימים</span>
        <span
          v-else
          class="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700"
        >מתקרב</span>
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto py-4">
      <!-- Visit info -->
      <div class="flex items-center gap-4 mb-3 text-xs text-surface-600 flex-wrap">
        <div class="flex items-center gap-1.5">
          <i class="pi pi-clock text-[13px]"></i>
          <span>מחזור: כל {{ formatCycle(suggestion.visitCycleDays) }}</span>
        </div>
        <div class="w-px h-3 bg-surface-200" />
        <div class="flex items-center gap-1.5">
          <i class="pi pi-calendar text-[13px]"></i>
          <span>ביקור אחרון: {{ formatDate(suggestion.lastVisitDate) }}</span>
        </div>
        <div class="w-px h-3 bg-surface-200" />
<!--        <div class="flex items-center gap-1.5">-->
<!--          <i class="pi pi-history text-[13px]"></i>-->
<!--          <span>{{ suggestion.totalVisits }} ביקורים אצלך</span>-->
<!--        </div>-->
      </div>

<!--      &lt;!&ndash; Reassurance: no upcoming appointment &ndash;&gt;-->
<!--      <div class="flex items-center gap-2 mb-4 px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">-->
<!--        <i class="pi pi-check-circle text-emerald-600 text-sm"></i>-->
<!--        <span class="text-xs text-emerald-700">ללא תור עתידי — בטוח לשלוח הודעה</span>-->
<!--      </div>-->

      <!-- Spam warning -->
      <div
        v-if="!suggestion.isInLoyaltyProgram"
        class="flex items-start gap-2 p-2.5 bg-orange-50 border border-orange-200 rounded-lg mb-4"
      >
        <i class="pi pi-exclamation-triangle text-orange-500 text-sm mt-0.5"></i>
        <span class="text-xs text-orange-700">
          לא חבר/ת מועדון. שליחת הודעות שיווקיות ללא הסכמה עלולה להיחשב כספאם.
        </span>
      </div>

      <!-- Actions -->
      <div class="flex flex-col gap-2">
        <Button
          label="שליחה ב-SMS"
          icon="pi pi-envelope"
          outlined
          class="w-full justify-center"
          @click="send('sms')"
          :loading="sendingChannel === 'sms'"
        />
        <Button
          label="שליחה ב-WhatsApp"
          icon="pi pi-whatsapp"
          severity="success"
          class="w-full justify-center"
          @click="send('whatsapp')"
          :loading="sendingChannel === 'whatsapp'"
        />
        <Button
          label="התעלם"
          icon="pi pi-times"
          text
          severity="secondary"
          class="w-full justify-center"
          @click="dismiss"
          :loading="dismissLoading"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ISmartNudgeSuggestion, ISmartNudgeSlot } from '@torim-app/types';
import { SmartNudgeChannelEnum } from '@torim-app/types';
import { ref } from 'vue';
import { CommunicationApi } from '@app/api/admin';
import { useDrawer } from '@torim-app/shared-components';
import { useToast } from 'vue-toastification';
import { DateTime } from 'luxon';
import { getStringHour } from '@torim-app/utils';

const props = defineProps<{
  suggestion: ISmartNudgeSuggestion;
  onDone: (action: 'sent' | 'dismissed') => void;
}>();

const { closeDrawer } = useDrawer();
const toast = useToast();

const sendingChannel = ref<string>();
const dismissLoading = ref(false);

const send = async (channel: 'sms' | 'whatsapp') => {
  sendingChannel.value = channel;
  try {
    await CommunicationApi.sendSmartNudge({
      suggestionId: props.suggestion.id!,
      channel: channel === 'whatsapp' ? SmartNudgeChannelEnum.WhatsApp : SmartNudgeChannelEnum.SMS,
    });
    toast.success(`ההודעה נשלחה בהצלחה ל-${props.suggestion.customerName}`);
    props.onDone('sent');
    closeDrawer();
  } catch (error) {
    toast.error('שליחת ההודעה נכשלה');
    console.error(error);
  } finally {
    sendingChannel.value = undefined;
  }
};

const dismiss = async () => {
  dismissLoading.value = true;
  try {
    await CommunicationApi.dismissSmartNudge(props.suggestion.id!);
    props.onDone('dismissed');
    closeDrawer();
  } catch (error) {
    toast.error('לא הצלחנו להתעלם מההצעה');
    console.error(error);
  } finally {
    dismissLoading.value = false;
  }
};

const formatCycle = (days: number): string => {
  if (days < 14) return `${days} ימים`;
  if (days < 30) return `${Math.round(days / 7)} שבועות`;
  return `${Math.round(days / 30)} חודשים`;
};

const formatDate = (date: Date | string): string => {
  return DateTime.fromISO(date as string)
    .setLocale('he')
    .toLocaleString(DateTime.DATE_MED);
};

const formatSlot = (slot: ISmartNudgeSlot): string => {
  const dt = DateTime.fromISO(slot.date).setLocale('he');
  return `${dt.toFormat('EEEE')} ${dt.toLocaleString(DateTime.DATE_SHORT)} ${getStringHour(slot.hour)}`;
};
</script>

<style scoped lang="scss">
.smart-nudge-action-drawer {
  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    i { font-size: 1.25rem; }
  }

  .sms-preview-phone {
    width: 260px;
    border-radius: 22px;
    border: 1px solid var(--p-surface-200);
    background: var(--p-surface-50);
    padding: 14px;
  }

  .sms-bubble {
    background: #fff;
    padding: 10px 14px;
    border-radius: 14px;
    border-top-right-radius: 4px;
    font-size: 12.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    box-shadow: 0 1px 1px rgba(0,0,0,0.04);
  }
}
</style>
